import React, { useState } from "react";
import { useAuth } from "./AuthContext";
import { X } from "@phosphor-icons/react";

// Lightweight self-contained auth modal triggered by clicking the PFP circle.
// No router needed — pure overlay over the DAW.

export default function AuthModal({ open, onClose, onSuccess, initialMode, headline }) {
  const { login, register } = useAuth();
  const [mode, setMode] = useState(initialMode || "signup"); // "login" | "signup"
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [username, setUsername] = useState("");
  const [igHandle, setIgHandle] = useState("");
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);

  // Reset to Create Account during render (not in an effect) so the stale "Welcome back" view never paints.
  const [wasOpen, setWasOpen] = useState(open);
  if (open !== wasOpen) {
    setWasOpen(open);
    if (open) { setMode(initialMode || "signup"); setErr(""); }
  }

  if (!open) return null;

  const fmt = (d) => {
    if (!d) return "Something went wrong.";
    if (typeof d === "string") return d;
    if (Array.isArray(d)) return d.map((e) => e?.msg || JSON.stringify(e)).join(" ");
    return d.msg || String(d);
  };

  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    setBusy(true);
    try {
      if (mode === "login") {
        await login(email.trim(), password);
      } else {
        await register({ email: email.trim(), password, username: username.trim() || undefined, instagram_handle: igHandle.trim() });
      }
      onClose();
      onSuccess?.(mode);
    } catch (e2) {
      setErr(fmt(e2.response?.data?.detail) || e2.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-[100] flex items-center justify-center"
      style={{ background: "rgba(0,0,0,0.72)", backdropFilter: "blur(8px)" }}
      onClick={onClose}
      data-testid="auth-modal"
    >
      <div
        className="relative w-[min(420px,92vw)] rounded-2xl p-7"
        style={{
          background: "linear-gradient(180deg,#101010,#070707)",
          border: "1px solid #2a2a2a",
          boxShadow: "0 30px 60px rgba(0,0,0,0.5), inset 0 1px 0 rgba(255,255,255,0.05)",
        }}
        onClick={(e) => e.stopPropagation()}
      >
        <button
          onClick={onClose}
          className="absolute top-4 right-4 text-white/40 hover:text-white"
          data-testid="auth-close-btn"
          aria-label="Close"
        >
          <X size={18} weight="bold" />
        </button>

        <div className="mb-5">
          <h2 className="font-display font-extrabold text-2xl tracking-tight" data-testid="auth-title">
            {mode === "login" ? "Welcome back" : "Create your account"}
          </h2>
          {headline && mode === "signup" ? (
            <p className="text-xs text-emerald-300 mt-2 font-semibold" data-testid="auth-headline">{headline}</p>
          ) : null}
          <p className="text-xs text-white/40 mt-1" data-testid="auth-subtext">
            {mode === "login" ? "Log in to save and share your beats." : "Generate beats in seconds — your first 2 beats are free. No card needed."}
          </p>
        </div>

        <form onSubmit={submit} className="space-y-3" data-testid="auth-form">
          <Field label="Email" value={email} onChange={setEmail} type="email" name="email" required testid="auth-email" />
          <Field label="Password" value={password} onChange={setPassword} type="password" name="password" required testid="auth-password" />
          {mode === "signup" && (
            <>
              <Field label="Display name (optional)" value={username} onChange={setUsername} testid="auth-username" />
              <Field label="Instagram handle (optional)" value={igHandle} onChange={setIgHandle} placeholder="@yourhandle" testid="auth-ig" />
            </>
          )}
          {err && <div className="text-xs text-red-400" data-testid="auth-error">{err}</div>}
          <button
            type="submit"
            disabled={busy}
            className="w-full py-2.5 rounded-lg font-semibold text-sm transition"
            style={{
              background: busy ? "#1a1a1a" : "linear-gradient(180deg,#fff,#d4d4d4)",
              color: busy ? "#666" : "#0a0a0a",
              cursor: busy ? "wait" : "pointer",
              border: "1px solid #303030",
              boxShadow: "0 2px 0 #000",
            }}
            data-testid="auth-submit"
          >
            {busy ? "…" : mode === "login" ? "Log in" : "Create account"}
          </button>
        </form>

        <div className="text-center mt-5 text-xs text-white/40">
          {mode === "login" ? (
            <>New here? <button type="button" className="underline text-white" onClick={() => { setMode("signup"); setErr(""); }} data-testid="auth-switch-signup">Create an account</button></>
          ) : (
            <>Already have an account? <button type="button" className="underline text-white" onClick={() => { setMode("login"); setErr(""); }} data-testid="auth-switch-login">Log in</button></>
          )}
        </div>
      </div>
    </div>
  );
}

function Field({ label, value, onChange, type = "text", name, required, placeholder, testid }) {
  return (
    <label className="block">
      <span className="text-[10px] uppercase tracking-wider text-white/40 mb-1 block font-mono">{label}</span>
      <input
        type={type}
        name={name}
        required={required}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        className="w-full px-3 py-2 rounded-md text-sm bg-[#0a0a0a] border border-[#262626] focus:outline-none focus:border-white/40 text-white"
        data-testid={testid}
      />
    </label>
  );
}
